
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Camera, Star, Loader2 } from 'lucide-react';
import { CampingSpot, AppUser } from '../../types';
import { addReview, db, markSpotConfirmed } from '../../services/firebaseService';
import { getSpotEnrichmentNeed } from '../../services/spotEnrichment';
import { doc, updateDoc } from 'firebase/firestore';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useToast } from '../../contexts/ToastContext';

export interface VerifyResult {
  // true når brukeren la inn plassens aller første ekte bilde — utløser førstemann-bonus.
  firstPhoto: boolean;
  photoCount: number;
}

interface VerifyAfterVisitModalProps {
  spot: CampingSpot;
  user: AppUser;
  onClose: () => void;
  onVerified: (result: VerifyResult) => void;
  // 'verify' (default): uverifisert plass — anmeldelsen verifiserer plassen.
  // 'review': allerede verifisert plass — be kun om anmeldelse, ikke rør isVerified.
  mode?: 'verify' | 'review';
}

const VerifyAfterVisitModal: React.FC<VerifyAfterVisitModalProps> = ({ spot, user, onClose, onVerified, mode = 'verify' }) => {
  const { t } = useTranslation();
  const toast = useToast();
  // Behovsstyrt: spør først og fremst om DET plassen mangler ("ikke-masete").
  const need = getSpotEnrichmentNeed(spot);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const trapRef = useFocusTrap<HTMLDivElement>();

  // Esc-to-close + focus trap + aria-modal (design-review finding C2).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleAddPhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setPhotos(prev => [...prev, ...files].slice(0, 5));
  };

  const handleSubmit = async () => {
    if (rating === 0) return;
    setSubmitting(true);
    try {
      await addReview(spot.id, user, rating, comment, photos.length > 0 ? photos : undefined, undefined, spot.name);
      if (mode === 'verify') {
        // Mark spot as verified
        const spotRef = doc(db, 'cached_spots', spot.id);
        await updateDoc(spotRef, { isVerified: true, verifiedAt: new Date() }).catch(() => {});
      }
      // Aktiv berikelse → frisk opp plassen i "Nylig bekreftet"-feeden, og
      // denormaliser bekrefteren så feed-avataren viser hvem som bekreftet.
      await markSpotConfirmed(spot.id, user);
      onVerified({ firstPhoto: need === 'photo' && photos.length > 0, photoCount: photos.length });
    } catch (err) {
      console.error('Verification submit failed', err);
      toast(t('error_occurred', 'En feil oppstod. Prøv igjen.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Portal til document.body. Kortet åpnes også fra SpotCard, som ligger inne i
  // SidebarPanel — den har `.glass-strong` (backdrop-filter) og z-mapOverlay
  // (2000). Backdrop-filter lager både en stacking context OG en containing
  // block for `position: fixed`, så uten portal blir z-[10004] låst inne i
  // panelets 2000-lag og MobileBottomNav (z-sticky = 3000) maler over
  // handlingsraden nederst — lagreknappen forsvant bak menylinja.
  return createPortal(
    // Bunnkort UTEN mørk backdrop: kartet (og pinnen som er skjøvet opp over
    // kortet) er synlig og interaktivt bak. Containeren slipper klikk gjennom
    // til kartet; bare selve kortet fanger dem.
    <div className="fixed inset-x-0 bottom-0 z-[10004] flex justify-center pointer-events-none">
      <div
        ref={trapRef}
        role="dialog"
        aria-modal="false"
        aria-label={mode === 'review' ? t('review_after_visit.title', 'Hvordan var plassen?') : t('spot.verify_title', 'Verifiser plass')}
        className="pointer-events-auto relative bg-[var(--bg-surface)] w-full sm:max-w-md max-h-[62dvh] sm:max-h-[70dvh] sm:mb-6 rounded-t-3xl sm:rounded-3xl border-t sm:border border-[var(--border-primary)] flex flex-col shadow-[0_-8px_40px_rgba(0,0,0,0.5)] animate-in slide-in-from-bottom-4 duration-300"
        onClick={e => e.stopPropagation()}
      >
        {/* Dra-håndtak (bunnark-affordans) */}
        <div className="flex justify-center pt-2.5 pb-1">
          <div className="w-9 h-1 rounded-full bg-[var(--border-secondary)]" />
        </div>
        <button
          onClick={onClose}
          className="absolute top-2 right-3 z-10 p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full text-[var(--text-muted)] hover:text-[var(--text-primary)]"
        >
          <X size={18} />
        </button>

        {/* Scrollbart innhold */}
        <div
          className="flex-1 overflow-y-auto px-6 pt-3"
          style={{ overscrollBehavior: 'contain' }}
        >
          <h3 className="text-[15px] font-black text-[var(--text-primary)] mb-1 pr-10">
            {mode === 'review'
              ? t('review_after_visit.title', 'Hvordan var plassen?')
              : need === 'photo' ? t('verify_after_visit.photo_title', 'Mangler bilde') : t('verify_after_visit.title')}
          </h3>
          <p className="text-tiny text-[var(--text-secondary)] mb-5">
            {mode === 'review'
              ? t('review_after_visit.message', 'Del en kort anmeldelse – det hjelper andre å velge plass.')
              : need === 'photo' ? t('verify_after_visit.photo_message', 'Denne plassen har ingen bilder ennå – ta du det første?') : t('verify_after_visit.message')}
          </p>

          {/* Spot name */}
          <div className="bg-[var(--bg-surface-alt)] rounded-xl px-3 py-2 mb-4">
            <span className="text-tiny font-bold text-[var(--text-primary)]">{spot.name || t('spot.unnamed')}</span>
          </div>

          {/* Star rating */}
          <div className="flex items-center gap-1 mb-4">
            {[1, 2, 3, 4, 5].map(i => (
              <button
                key={i}
                onClick={() => setRating(i)}
                className="p-1.5 transition-transform active:scale-125"
              >
                <Star
                  size={28}
                  className={i <= rating ? 'text-amber-400' : 'text-[var(--text-muted)]'}
                  fill={i <= rating ? 'currentColor' : 'none'}
                />
              </button>
            ))}
          </div>

          {/* Comment */}
          <textarea
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder={t('verify_after_visit.comment_placeholder')}
            className="w-full bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-xl px-3 py-2 text-tiny text-[var(--text-primary)] placeholder-[var(--text-muted)] resize-none mb-3"
            rows={2}
          />

          {/* Photo upload */}
          <button
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-[var(--border-secondary)] text-2xs font-bold text-[var(--text-secondary)] hover:border-emerald-500 hover:text-emerald-500 transition-colors mb-4 w-full justify-center"
          >
            <Camera size={14} />
            {photos.length > 0 ? `${photos.length} ${t('verify_after_visit.photos_selected')}` : t('verify_after_visit.add_photo')}
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleAddPhotos} />
          {need === 'photo' && (
            <p className="text-3xs font-bold text-emerald-500 -mt-2 mb-4 text-center">
              {t('verify_after_visit.first_photo_bonus', 'Første bilde på plassen gir ekstra bonuspoeng! 📸')}
            </p>
          )}
        </div>

        {/* Handlinger – festet nederst */}
        <div
          className="flex gap-2 px-6 pt-3 border-t border-[var(--border-primary)]"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
        >
          <button
            onClick={handleSubmit}
            disabled={rating === 0 || submitting}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl font-black text-2xs uppercase tracking-widest transition-all bg-emerald-600 text-white hover:bg-emerald-500 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : t('verify_after_visit.submit')}
          </button>
          <button
            onClick={onClose}
            className="px-6 py-3 rounded-2xl font-black text-2xs uppercase tracking-widest text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
          >
            {t('verify_after_visit.skip')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default VerifyAfterVisitModal;
